import {
	Currency,
	Dtos,
	formatToDollar,
	type NaicsCodeId,
	NaicsCodes,
	type SuretyAccountId,
	SuretyType,
	Types,
} from "@inrev/common";
import {
	type AdminAccountCompany,
	type AdminWipStatement,
	useAdminFetchAccount,
	useAdminFetchAccountCompanies,
	useAdminFetchAccountMetadata,
	useAdminFetchAccountSpecialtyCodes,
	useAdminFetchAccountWorkInProgressStatements,
	useAdminUpdateAccountNaicsCodes,
	useAdminUpdateMetadataInline,
} from "@portal/api/account/admin";
import { LEGACY_NAICS_VERSION_YEAR, useFetchNaicsCodes } from "@portal/api/naics-code";
import { AccountSpecialtyField } from "@portal/components/features/account/AccountSpecialtyField";
import {
	HistoryCurrencyCard,
	type HistoryFieldSubmitCallbacks,
	HistoryReadOnlyCard,
	HistoryYesNoCard,
} from "@portal/components/features/account/history";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { Card, Text } from "@portal/components/primitives";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { ErrorDisplay } from "@portal/components/ui/ErrorDisplay";
import { MutationErrorFlag } from "@portal/components/ui/MutationErrorFlag";
import { useShowAccountContractHistory } from "@portal/hooks/useShowAccountContractHistory";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";
import { formatEmpty } from "../../../../../utils";

type ContractMetadataRequest = Extract<
	Dtos.Admin.SuretyAccount.Update.Metadata.Request,
	{ suretyType: typeof SuretyType.contract }
>;
type ContractMetadataUpdate = Partial<
	Omit<ContractMetadataRequest, "suretyType" | "updatedAt" | "contract">
> & {
	contract?: Partial<NonNullable<ContractMetadataRequest["contract"]>>;
};

type AccountMetadata = Dtos.Admin.SuretyAccount.Metadata.Get.Response;
type AccountSpecialtyCodes = ReturnType<typeof useAdminFetchAccountSpecialtyCodes>;
type NaicsCatalog = ReturnType<typeof useFetchNaicsCodes>;

type YesNoCard = {
	testId: string;
	label: string;
	value: boolean | null;
	set: (draft: boolean | null) => ContractMetadataUpdate;
};

const UPDATE_ERROR_HEADING = "Update Capacity Error";
const CATALOG_UNAVAILABLE_ERROR = new Error("Unable to load NAICS catalog");

const CapacityCards = ({
	accountId,
	updatedAt,
	primaryCompany,
	metadata,
	wipStatements,
	specialty,
	catalog,
}: {
	accountId: SuretyAccountId;
	updatedAt: Dtos.Admin.Account.Get.Response["updatedAt"];
	primaryCompany?: AdminAccountCompany;
	metadata: AccountMetadata;
	wipStatements: AdminWipStatement[];
	specialty: AccountSpecialtyCodes;
	catalog: NaicsCatalog;
}) => {
	const showContractHistory = useShowAccountContractHistory(
		Types.SuretyAccount.Contract.hasContractHistoryMetadata(metadata.contract),
	);
	const { updateMetadata } = useAdminUpdateMetadataInline(accountId);

	const saveField = useCallback(
		(override: ContractMetadataUpdate, callbacks?: HistoryFieldSubmitCallbacks) => {
			updateMetadata(
				(lock) => ({
					suretyType: SuretyType.contract,
					updatedAt: lock.updatedAt,
					...lock.metadata,
					...override,
					contract: {
						...lock.metadata?.contract,
						...override.contract,
					},
				}),
				{ updatedAt, metadata },
				callbacks,
			);
		},
		[metadata, updateMetadata, updatedAt],
	);

	const {
		rows: specialtyRows,
		isLoading: specialtyCodesLoading,
		isFetching: specialtyCodesFetching,
		error: specialtyCodesError,
		refetch: refetchSpecialtyCodes,
	} = specialty;
	const specialtyIds = specialtyRows.map((row) => row.id);
	const {
		naicsCodes: catalogRows,
		naicsCodesError: catalogError,
		naicsCodesIsFetching: catalogFetching,
		refetchNaicsCodes,
	} = catalog;
	const { updateAccountNaicsCodes, updateAccountNaicsCodesError } =
		useAdminUpdateAccountNaicsCodes(accountId);
	const [naicsDraft, setNaicsDraft] = useState<NaicsCodeId[]>(specialtyIds);
	const [naicsPickerOpen, setNaicsPickerOpen] = useState(false);
	const naicsPickerOpenRef = useRef(naicsPickerOpen);
	naicsPickerOpenRef.current = naicsPickerOpen;
	useEffect(() => {
		if (naicsPickerOpenRef.current) return;
		if (specialtyCodesLoading) return;
		setNaicsDraft((draft) => (NaicsCodes.areSame(draft, specialtyIds) ? draft : specialtyIds));
	}, [specialtyIds, specialtyCodesLoading]);

	const persistNaics = useCallback(() => {
		if (NaicsCodes.areSame(naicsDraft, specialtyIds)) return;
		updateAccountNaicsCodes({ naicsCodeIds: naicsDraft });
	}, [naicsDraft, specialtyIds, updateAccountNaicsCodes]);

	const wipStatement = wipStatements[0];
	const backlogLabel = `Current Backlog Cost to Complete${
		wipStatement ? ` (${DateTime.fromISO(wipStatement.createdAt).toFormat("MM/dd/yyyy")})` : ""
	}`;

	const alwaysVisibleYesNoCards: YesNoCard[] = [
		{
			testId: "account-loss-to-surety",
			label: "Caused Surety Loss",
			value: metadata.causedSuretyLoss ?? null,
			set: (causedSuretyLoss) => ({ causedSuretyLoss }),
		},
	];
	const contractHistoryYesNoCards: YesNoCard[] = [
		{
			testId: "account-lost-payment-lawsuit",
			label: "Lost Payment Suit",
			value: metadata.contract?.lostAPaymentSuit ?? null,
			set: (lostAPaymentSuit) => ({ contract: { lostAPaymentSuit } }),
		},
		{
			testId: "account-failed-project",
			label: "Failed to Complete Job",
			value: metadata.contract?.failedToCompleteAProject ?? null,
			set: (failedToCompleteAProject) => ({ contract: { failedToCompleteAProject } }),
		},
		{
			testId: "account-gross-loss-projects-36mo",
			label: "Gross Loss on Job in Last 36 Mo.",
			value: metadata.contract?.constructionProjectsGrossLossGt10PercentLast36Months ?? null,
			set: (constructionProjectsGrossLossGt10PercentLast36Months) => ({
				contract: { constructionProjectsGrossLossGt10PercentLast36Months },
			}),
		},
		{
			testId: "account-current-expected-loss",
			label: "Job in Progress with Expected Gross Loss",
			value: metadata.contract?.currentConstructionProjectExpectedGrossLoss ?? null,
			set: (currentConstructionProjectExpectedGrossLoss) => ({
				contract: { currentConstructionProjectExpectedGrossLoss },
			}),
		},
	];
	const visibleYesNoCards = showContractHistory
		? [...alwaysVisibleYesNoCards, ...contractHistoryYesNoCards]
		: alwaysVisibleYesNoCards;

	return (
		<div className="flex flex-col gap-[20px]">
			{specialtyCodesError ? (
				<ReportedRetryableLoadError
					error={specialtyCodesError}
					resource="account specialty"
					onRetry={() => refetchSpecialtyCodes()}
					retrying={specialtyCodesFetching}
					testId="account-specialty-load-error"
				/>
			) : catalogError || catalogRows === undefined ? (
				<ReportedRetryableLoadError
					error={catalogError ?? CATALOG_UNAVAILABLE_ERROR}
					resource="NAICS catalog"
					onRetry={() => refetchNaicsCodes()}
					retrying={catalogFetching}
					testId="account-specialty-catalog-load-error"
				/>
			) : (
				<AccountSpecialtyField
					value={naicsDraft}
					onChange={setNaicsDraft}
					onBlur={persistNaics}
					open={naicsPickerOpen}
					onOpenChange={setNaicsPickerOpen}
				/>
			)}
			<MutationErrorFlag
				errorHeading={UPDATE_ERROR_HEADING}
				mutationError={updateAccountNaicsCodesError}
			/>
			<Card elevation="raised" className="rounded-xl" data-testid="admin-account-capacity-history">
				<Card.Body className="flex w-full flex-col p-6 space-y-5">
					<Text type="title" color="primary">
						History
					</Text>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
						{showContractHistory && (
							<HistoryCurrencyCard
								testId="account-largest-project"
								label="Largest Job Completed"
								value={metadata.contract?.largestProjectCompleted ?? null}
								errorHeading={UPDATE_ERROR_HEADING}
								submit={(draft, callbacks) =>
									saveField(
										{
											contract: {
												largestProjectCompleted: draft === null ? null : Currency.round(draft),
											},
										},
										callbacks,
									)
								}
							/>
						)}
						<HistoryReadOnlyCard testId="account-years-in-business" label="Years in Business">
							{formatEmpty(primaryCompany?.creditReports[0]?.yearsInBusinessOrCreditAge)}
						</HistoryReadOnlyCard>
						{visibleYesNoCards.map(({ testId, label, value, set }) => (
							<HistoryYesNoCard
								key={testId}
								testId={testId}
								label={label}
								value={value}
								errorHeading={UPDATE_ERROR_HEADING}
								submit={(draft, callbacks) => saveField(set(draft), callbacks)}
							/>
						))}
						<HistoryReadOnlyCard testId="account-current-backlog" label={backlogLabel}>
							{formatEmpty(formatToDollar(wipStatement?.projectBacklogCost))}
						</HistoryReadOnlyCard>
					</div>
				</Card.Body>
			</Card>
		</div>
	);
};

export const AdminAccountCapacitySection = ({ accountId }: { accountId: SuretyAccountId }) => {
	const { account, accountError, accountIsLoading } = useAdminFetchAccount(accountId);
	const { companies, companiesError, companiesIsLoading } =
		useAdminFetchAccountCompanies(accountId);
	const { metadata, metadataError, metadataIsLoading } = useAdminFetchAccountMetadata(accountId);
	const { wipStatements, wipStatementsError, wipStatementsIsLoading } =
		useAdminFetchAccountWorkInProgressStatements(accountId);
	const specialty = useAdminFetchAccountSpecialtyCodes(accountId);
	const catalog = useFetchNaicsCodes({ versionYear: LEGACY_NAICS_VERSION_YEAR });

	return (
		<div data-testid="admin-account-capacity-section" className="w-full flex flex-col space-y-5">
			<Text type="heading" color="primary">
				Capacity
			</Text>
			{accountError ? (
				<ErrorDisplay error={accountError} />
			) : metadataError ? (
				<ErrorDisplay error={metadataError} />
			) : companiesError ? (
				<ErrorDisplay error={companiesError} />
			) : wipStatementsError ? (
				<ErrorDisplay error={wipStatementsError} />
			) : !account ||
				!companies ||
				!metadata ||
				!wipStatements ||
				accountIsLoading ||
				companiesIsLoading ||
				metadataIsLoading ||
				wipStatementsIsLoading ||
				specialty.isLoading ||
				catalog.naicsCodesIsLoading ? (
				<CenteredSpinner />
			) : (
				<CapacityCards
					accountId={accountId}
					updatedAt={account.updatedAt}
					primaryCompany={companies.find((company) => company.id === account.primaryCompanyId)}
					metadata={metadata}
					wipStatements={wipStatements}
					specialty={specialty}
					catalog={catalog}
				/>
			)}
		</div>
	);
};
